import { ComponentBase } from './ComponentBase';
import { template, unsafeHTML } from '../utils/TemplateParser';
import { lang } from '../lang';
import { vars } from '../utils/vars';
import { api } from '../utils/api';
import { DateTime } from 'luxon';
import { load as loadCAPTCHA } from 'recaptcha-v3';

const registerActions = {
    REGISTER: 'REGISTER',
    CANCEL: 'CANCEL',
};

export class RSVP extends ComponentBase {
    constructor(model, view) {
        super();
        this.view = view;
        this.model = model;
        this.render();
    }

    registerEvents() {
        return {
            'click  [data-action="toggle-rsvp"]' : 'toggleRSVP',
            'submit  [data-action="submit-rsvp"]' : 'submitRSVP',
            'keypress  #rsvp-form input' : 'clearError',
        };
    }

    template() {
        const isEventDetailsPage = this.view === 'event-detail-page' ? true : false;
        if(!vars.pubcalendar.rsvp || !this.model.rsvp) return ''; // rsvp disabled
        if(this.model.rsvpAvailable < 1 && !this.model.hasRsvpEmails) return ''; // no more seats left and no cancel option available
        if(this.model.recurring && isEventDetailsPage) return ''; // Series and Event Details Page

        const now = DateTime.utc().toISO();
        const rsvpStart = DateTime.fromISO(this.model.rsvpStart, {zone: 'UTC'}).toISO();
        const rsvpEnd = DateTime.fromISO(this.model.rsvpEnd, {zone: 'UTC'}).toISO();

        if(rsvpStart && rsvpStart > now) return ''; // rsvp has not started yet
        if(rsvpEnd && rsvpEnd < now) return ''; // rsvp already ended

        let rsvpOptions;
        if (this.model.hasRsvpEmails) {
            if (this.model.rsvpAvailable < 1) {
                rsvpOptions = template`
                    <div class="form-group">
                        <div class="radio-group">
                            <label><input type="radio" id="rsvp-cancel" name="action" value="${registerActions.CANCEL}" checked> ${lang.CancelAttendance}</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <button type="submit">${lang.Submit}</button>
                    </div>
                `;
            }
            else {
                rsvpOptions = template`
                    <div class="form-group">
                        <div class="radio-group">
                            <label><input type="radio" id="rsvp-register" name="action" value="${registerActions.REGISTER}" checked> ${lang.RegisterToAttend}</label>
                            <label><input type="radio" id="rsvp-cancel" name="action" value="${registerActions.CANCEL}"> ${lang.CancelAttendance}</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <button type="submit">${lang.Submit}</button>
                    </div>
                `;
            }
        }
        // In the future, if we want to allow attendees to cancel their RSVPs even when emails are disabled
        // else if (!this.model.hasRsvpEmails && !this.model.rsvpAvailable) {
        //     rsvpOptions = template`
        //         <div class="form-group">
        //             <div class="radio-group hidden">
        //                 <label><input type="radio" id="rsvp-cancel" name="action" value="${registerActions.CANCEL}" checked> ${lang.CancelAttendance}</label>
        //             </div>
        //         </div>
        //         <div class="form-group">
        //             <button type="submit">${lang.CancelAttendance}</button>
        //         </div>
        //     `;
        // }
        else {
            rsvpOptions = template`
                <div class="form-group">
                    <button type="submit">${lang.Register}</button>
                </div>
            `;
        }

        const spotsAvailable = this.model.rsvpAvailable ?
            template`<div class="spots-available">${lang.SpotsAvailable.replace('{{spots}}', this.model.rsvpAvailable)}</div>` : '';

        // Add form group option with radio options for register or cancel
        return template`
            <div class="detail-with-icon rsvp" tabindex="0" aria-label="${lang.RSVP}">
                <i class="icon far fa-calendar-check"></i>
                <div class="register">
                    ${spotsAvailable}
                    <a href="javascript:;" id="rsvp-toggle" data-action="toggle-rsvp" aria-controls="rsvp-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="${lang.ToggleRSVPform}">${lang.RSVP}</a>
                    <div class="message"></div>
                </div>
            </div>
            <div id="rsvp-wrapper" class="rsvp-wrapper" aria-hidden="true">
                <div class="instructions"><span>${lang.RSVPFormInstructions}</span> <a href="javascript:;" class="close" data-action="toggle-rsvp" aria-label="${lang.CloseRSVPForm}" title="${lang.CloseRSVPForm}"><i class="icon fas fa-times"></i></a></div>
                <form id="rsvp-form" data-action="submit-rsvp" autocomplete="off">
                    <div class="form-group">
                        <label for="rsvp-name">${lang.Name}</label>
                        <input type="text" id="rsvp-name" name="name" value="" maxlength="128">
                        <span id="error-block-rsvp-name" class="error-block">
                            <i class="icon fas fa-info-circle"></i> <span></span>
                        </span>
                    </div>
                    <div class="form-group">
                        <label for="rsvp-email">${lang.EmailAddress}</label>
                        <input type="text" id="rsvp-email" name="email" value="" maxlength="255">
                        <span id="error-block-rsvp-email" class="error-block">
                            <i class="icon fas fa-info-circle"></i> <span></span>
                        </span>
                    </div>
                    ${rsvpOptions}
                    <div class="google-recaptcha-statement">${unsafeHTML(lang.GoogleRecaptchaStatement)}</div>
                </form>
                <div class="backdrop"></div>
                <div class="loader-ring"><div></div><div></div><div></div><div></div></div>
            </div>
        `;
    }

    domReady() {
        this.elements = {
            rsvp: this.el.querySelector('.rsvp'),
            rsvpWrapper: this.el.querySelector('.rsvp-wrapper'),
            rsvpForm: this.el.querySelector('#rsvp-form'),
            rsvpToggle: this.el.querySelector('#rsvp-toggle'),
            rsvpMessage: this.el.querySelector('.rsvp .message'),
        };
    }

    /* Event Handlers */
    toggleRSVP() {
        const wrapper = this.elements.rsvpWrapper;
        const collapse = wrapper.style.display == 'block';
        
        wrapper.style.display = collapse ? 'none' : 'block';
        wrapper.setAttribute('aria-hidden', collapse);
        this.elements.rsvpToggle.setAttribute('aria-expanded', !collapse);
    }

    clearError(e) {
        e.target.closest('.form-group').classList.remove('error');
    }

    submitRSVP(e) {
        e.preventDefault();
        const formData = Object.fromEntries(new FormData(this.elements.rsvpForm));

        // Only include action in data if it is not the default (REGISTER)
        if (formData.action === registerActions.REGISTER) {
            delete formData.action;
        }
        
        const data = {
            eventId: this.model.id,
            ...formData,
        };

        // Add page URL and hash boolean to data
        data.hash = window.location.hash ? true : false;
        data.url = api.getURL();

        if(!this.#validate(data)) return;

        this.elements.rsvpWrapper.classList.add('submitting');

        const options = {
            useRecaptchaNet: true,
            autoHideBadge: true,
        };

        loadCAPTCHA(vars.pubcalendar.captchaSiteKey, options).then(recaptcha => {
            recaptcha.execute('register_rsvp').then(token => {
                data.token = token;

                api.submitRSVP(data).then(() => {
                    let message = '';
                    if (data.action && data.action === registerActions.CANCEL) {
                        message = lang.RSVPCancelConfirmation;
                    }
                    else {
                        if (this.model.hasRsvpEmails) {
                            message = lang.RSVPSubmitConfirmation;
                        }
                        else {
                            message = lang.ThankYouForRegistering;
                        }
                    }
                    this.#displayMessage(message);
                })
                .catch(response => {
                    if(!(response instanceof Error) && (response.name || response.email)) {
                        this.#displayInlineErrors(response);
                        this.elements.rsvpWrapper.classList.remove('submitting');
                    } else {
                        this.#displayMessage(response || lang.RSVPGenericError, 'error');
                    }
                });

            });
        }).catch(response => {
            this.#displayMessage(lang.RSVPRecaptchaError, 'error');
        });
    }

    /* Private Methods */
    #validate(data){
        const errors = {};
        this.#trimData(data);

        if(data.name == '') errors.name = lang.RSVPInvalidName;

        if(!/^[^@]+@[^@]+\.[^@.]+$/.test(data.email)){
            errors.email = lang.RSVPInvalidEmail;
        }

        this.#displayInlineErrors(errors);

        return !Object.keys(errors).length; // If no errors exist return true (valid)
    }

    #trimData(obj) {
        Object.keys(obj).forEach(k => {
            if(typeof obj[k] === 'string') obj[k] = obj[k].trim();
        });
    }

    #displayInlineErrors(errors) {
        for(const [field, message] of Object.entries(errors)) {
            const formGroup = this.elements.rsvpForm.querySelector(`input[name="${field}"]`).closest('.form-group');
            formGroup.classList.add('error');
            formGroup.querySelector('.error-block span').innerText = message;
        }
    }

    #displayMessage(message, type = 'success') {
        this.elements.rsvpWrapper.style.display = 'none';
        this.elements.rsvp.classList.add(type);
        this.elements.rsvpMessage.innerText = message;
    }
}